import React, { useEffect } from "react";

import { Control, useWatch } from "react-hook-form";

import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import useAddressApi from "../../../../../hooks/useAddressApi";
import ReactHookInput from "../../../../shared/forms/ReactHookInput";
import ReactHookSelect from "../../../../shared/forms/ReactHookSelect";
import useSnackbar from "../../../../shared/Snackbar/store";
import useCheckoutStore, { iCheckoutForm } from "../../Checkout/store";

type ZipcodeFields = "billingAddress.zipcode" | "shippingAddress.zipcode";
type CityFields = "billingAddress.city" | "shippingAddress.city";
type StreetFields = "billingAddress.street" | "shippingAddress.street";
type HouseNumberFields =
    | "billingAddress.houseNumber"
    | "shippingAddress.houseNumber";
type CountryFields = "billingAddress.countryId" | "shippingAddress.countryId";

interface iAddressComponentProps {
    control: Control<iCheckoutForm>;
    zipcodeField: ZipcodeFields;
    cityField: CityFields;
    streetField: StreetFields;
    houseNumberField: HouseNumberFields;
    countryField: CountryFields;
}

const ZipcodeCityInline = styled.div`
    display: grid;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        grid-template-columns: 1fr 2fr;
        gap: 0.75em;
    `}
`;

const StreetHouseNumberInline = styled.div`
    display: grid;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        grid-template-columns: 3fr 1fr;
        gap: 0.75em;
    `}
`;

const AddressPicker = ({
    control,
    zipcodeField,
    cityField,
    streetField,
    houseNumberField,
    countryField,
}: iAddressComponentProps) => {
    const zipcode = useWatch({
        control,
        name: zipcodeField,
    });

    const city = useWatch({
        control,
        name: cityField,
    });

    const { cities, streets, addressApiError } = useAddressApi({
        zipcode: zipcode,
        city: city,
    });

    const { countries, fetchCountries } = useCheckoutStore(
        (store) => ({
            countries: store.countries,
            fetchCountries: store.fetchCountries,
        }),
        shallow
    );

    useEffect(() => {
        if (!countries || countries.length === 0) {
            fetchCountries();
        }
    }, []);

    useEffect(() => {
        if (addressApiError) {
            notify(
                "Fehler",
                addressApiError?.message ??
                    "Es ist ein Fehler bei der Abfrage der Adressen aufgetreten."
            );
        }
    }, [addressApiError]);

    const { notify } = useSnackbar();

    return (
        <>
            <ZipcodeCityInline>
                <ReactHookInput
                    name={zipcodeField}
                    placeholder="Postleitzahl"
                    rules={{ required: true }}
                    control={control}
                />
                <ReactHookSelect
                    name={cityField}
                    placeholder="Ort"
                    rules={{ required: true }}
                    control={control}
                    isDisabled={cities.length < 1}
                    options={cities.map((city) => ({
                        value: city.ONAME,
                        text: city.ONAME,
                    }))}
                />
            </ZipcodeCityInline>

            <StreetHouseNumberInline>
                <ReactHookSelect
                    name={streetField}
                    placeholder="Straße"
                    rules={{ required: true }}
                    control={control}
                    isDisabled={streets.length < 1}
                    options={streets.map((street) => ({
                        value: street,
                        text: street,
                    }))}
                />
                <ReactHookInput
                    name={houseNumberField}
                    type="text"
                    placeholder="Hausnummer"
                    rules={{
                        required: true,
                        maxLength: 5,
                    }}
                    control={control}
                />
            </StreetHouseNumberInline>

            <ReactHookSelect
                name={countryField}
                placeholder="Land"
                control={control}
                rules={{ required: true }}
                options={countries.map((country) => ({
                    text: country.name,
                    value: country.id,
                }))}
            />
        </>
    );
};

export default AddressPicker;
